/**
 * Hook for dynamic transaction attributes loading
 * Replaces hardcoded TRANSACTION_TYPES constants with API-driven data
 * Uses React Query for caching and deduplication
 */

import { useMemo, useCallback } from 'react'

import type { TransactionAttributeResponse, GetTransactionAttributesRequest } from '@bat/shared'
import { useTranslation } from '@platform/i18n/hooks/useTranslation'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { defaultQueryRetry } from '@/platform/query-client'

import { transactionAttributesApiService } from '../services/transaction-attributes-api.service'
import { classifyAttributeDirection } from '../utils/attribute-direction.utils'

// Query key factory for transaction attributes
export const transactionAttributeKeys = {
  all: ['transaction-attributes'] as const,
  list: (locale: string, category?: string, activeOnly?: boolean) =>
    [...transactionAttributeKeys.all, locale, { category, activeOnly }] as const,
}

export interface UseTransactionAttributesReturn {
  // Data
  attributes: TransactionAttributeResponse[]
  attributesGrouped: Record<string, TransactionAttributeResponse[]>
  categories: string[]

  // Loading states
  isLoading: boolean
  isError: boolean
  error: string | null

  // Actions
  refetch: () => Promise<void>
  getByKey: (key: string) => TransactionAttributeResponse | undefined

  // Classification helpers (derived from the DB catalog's requiresIncoming/requiresOutgoing)
  isDepositAttribute: (key: string) => boolean
  isWithdrawalAttribute: (key: string) => boolean

  // Cache management
  clearCache: () => void
}

export const useTransactionAttributes = (
  options?: GetTransactionAttributesRequest,
): UseTransactionAttributesReturn => {
  const { i18n } = useTranslation()
  const locale = i18n.language
  const queryClient = useQueryClient()

  const {
    data,
    isLoading,
    isError,
    error: queryError,
    refetch: queryRefetch,
  } = useQuery({
    queryKey: transactionAttributeKeys.list(locale, options?.category, options?.activeOnly),
    queryFn: () =>
      transactionAttributesApiService.getAll({
        locale,
        category: options?.category,
        activeOnly: options?.activeOnly,
      }),
    staleTime: 5 * 60 * 1000, // 5 minutes — matches the old manual cache TTL
    gcTime: 10 * 60 * 1000,
    retry: defaultQueryRetry,
  })

  // Memoized: the `?? []` fallback would otherwise mint a new array every render
  // while loading, churning every downstream memo/callback keyed on these.
  const attributes = useMemo(() => data?.attributes ?? [], [data])
  const categories = useMemo(() => data?.categories ?? [], [data])

  /**
   * Get attributes grouped by category
   */
  const attributesGrouped = useMemo(() => {
    const grouped: Record<string, TransactionAttributeResponse[]> = {}

    attributes.forEach((attr) => {
      if (!grouped[attr.category]) {
        grouped[attr.category] = []
      }
      grouped[attr.category].push(attr)
    })

    // Sort attributes within each category by sortOrder
    Object.keys(grouped).forEach((category) => {
      grouped[category].sort((a, b) => a.sortOrder - b.sortOrder)
    })

    return grouped
  }, [attributes])

  /**
   * Get attribute by key
   */
  const getByKey = useCallback(
    (key: string) => {
      return attributes.find((attr) => attr.key === key)
    },
    [attributes],
  )

  /**
   * Deposit-type attribute: requiresIncoming=true and requiresOutgoing=false.
   * Returns false when the key is not found (graceful when data not yet loaded).
   */
  const isDepositAttribute = useCallback(
    (key: string) => {
      const attr = attributes.find((a) => a.key === key)
      if (!attr) return false
      return classifyAttributeDirection(attr.requiresIncoming, attr.requiresOutgoing) === 'positive'
    },
    [attributes],
  )

  /**
   * Withdrawal-type attribute: requiresOutgoing=true and requiresIncoming=false.
   * Returns false when the key is not found (graceful when data not yet loaded).
   */
  const isWithdrawalAttribute = useCallback(
    (key: string) => {
      const attr = attributes.find((a) => a.key === key)
      if (!attr) return false
      return classifyAttributeDirection(attr.requiresIncoming, attr.requiresOutgoing) === 'negative'
    },
    [attributes],
  )

  /**
   * Refetch data
   */
  const refetch = useCallback(async () => {
    await queryRefetch()
  }, [queryRefetch])

  /**
   * Clear cache — invalidates the React Query cache and the service's internal cache
   */
  const clearCache = useCallback(() => {
    transactionAttributesApiService.clearCache()
    queryClient.invalidateQueries({ queryKey: transactionAttributeKeys.all })
  }, [queryClient])

  const error = queryError instanceof Error ? queryError.message : null

  return useMemo(
    (): UseTransactionAttributesReturn => ({
      // Data
      attributes,
      attributesGrouped,
      categories,

      // Loading states
      isLoading,
      isError,
      error,

      // Actions
      refetch,
      getByKey,

      // Classification helpers
      isDepositAttribute,
      isWithdrawalAttribute,

      // Cache management
      clearCache,
    }),
    [
      attributes,
      attributesGrouped,
      categories,
      isLoading,
      isError,
      error,
      refetch,
      getByKey,
      isDepositAttribute,
      isWithdrawalAttribute,
      clearCache,
    ],
  )
}

/**
 * OFFERABLE catalog — ACTIVE rows only.
 *
 * Use this wherever the user is CHOOSING a value: the attribute picker, bulk
 * edit, filters, the bridge wizard. A deactivated attribute must never come
 * back here — that is the first half of the super-admin deactivate promise
 * ("ya no aparecerá en nuevas transacciones").
 */
export const useOfferableTransactionAttributes = (
  options?: Omit<GetTransactionAttributesRequest, 'activeOnly'>,
): UseTransactionAttributesReturn =>
  // `activeOnly: true` is passed EXPLICITLY rather than relying on the server
  // default, so this stays correct even if that default ever changes.
  useTransactionAttributes({ ...options, activeOnly: true })

/**
 * RENDERABLE catalog — every row, ACTIVE **and** DEACTIVATED.
 *
 * Use this to resolve the label of an attribute that is ALREADY PERSISTED on a
 * transaction: the table's "Atributo" cell, the transaction modal header, the
 * exported "Atributo" column, merge validation. A row created before its
 * attribute was deactivated still references that key, and the offerable
 * catalog cannot resolve it — the label would degrade to the raw key
 * (`zz-hunt-probe`), on screen AND in the CSV, breaking the second half of the
 * deactivate promise ("las transacciones existentes … seguirán intactas").
 *
 * NEVER use this to populate a picker: it would put deactivated attributes back
 * in front of the user. That is exactly why the two questions have two hooks
 * instead of one flipped default.
 */
export const useTransactionAttributeCatalog = (
  options?: Omit<GetTransactionAttributesRequest, 'activeOnly'>,
): UseTransactionAttributesReturn => useTransactionAttributes({ ...options, activeOnly: false })

// Convenience hook for getting just the attribute keys as constants-like object
export const useTransactionAttributeTypes = () => {
  const { attributes, isLoading, isError } = useOfferableTransactionAttributes()

  const types = useMemo(() => {
    const result: Record<string, string> = {}
    attributes.forEach((attr) => {
      // Create UPPER_CASE key for compatibility with old constants usage
      const constantKey = attr.key.toUpperCase().replace(/-/g, '_')
      result[constantKey] = attr.key
    })
    return result
  }, [attributes])

  return { types, isLoading, isError }
}
